import type { MediaItem } from '../api/types'
import { formatDuration } from '../lib/format'
import type { QueueItem } from '../upload/db'
import { Spinner } from './icons'

/**
 * Het fotogrid: drie kolommen op 390px, vijf vanaf lg. De tegels die nog aan
 * het uploaden zijn staan vooraan met een waas eroverheen — dat is ronde 6A,
 * en het is ook eerlijk: ze staan bovenaan zodra ze er echt zijn.
 */
export function MediaGrid({
  items,
  pending,
  showNames = false,
  onOpen,
}: {
  items: MediaItem[]
  pending: QueueItem[]

  /**
   * Zet de naam van wie het uploadde op de tegel.
   *
   * Alleen in de tab waar alles door elkaar staat. In "Mijn foto's" is het
   * antwoord altijd jij, en in "Video's" staat rechtsonder al de duur — twee
   * labels op één tegel van 120px is één te veel.
   */
  showNames?: boolean

  onOpen: (id: string) => void
}) {
  return (
    <div className="grid grid-cols-3 gap-0.75 lg:grid-cols-5 lg:gap-1.5">
      {pending.map((item) => (
        <PendingTile key={item.id} item={item} />
      ))}

      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          onClick={() => onOpen(item.id)}
          className="placeholder-warm rounded-tile lg:rounded-tile-lg relative aspect-square overflow-hidden"
        >
          <img
            src={item.webUrl}
            alt={`Van ${item.guestName}`}
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover"
          />

          {item.kind === 'Video' && (
            <span className="rounded-tile bg-shell/85 text-ink-muted absolute bottom-1.5 left-1.5 px-1.5 py-0.5 text-[11px] lg:bottom-2 lg:left-2 lg:text-xs">
              {formatDuration(item.durationSeconds) ?? 'video'}
            </span>
          )}

          {/*
            Van wie is dit. Op een verloop en niet op een blokje: een tegel is
            een foto en die kan alles zijn — een witte bruidsjurk, een donkere
            dansvloer — dus een half doorzichtig vlakje is soms leesbaar en soms
            niet. Het verloop van onderaf is dat altijd, en het dekt de foto
            minder af dan een balk.

            Rechts uitgelijnd, zodat hij de duur van een video links niet
            raakt, en op één regel afgekapt: "Jasper & Nele" past, een langere
            naam wordt netjes korter in plaats van de tegel in te nemen.
          */}
          {showNames && (
            <span className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/62 to-transparent pt-6 pb-1 pl-8 lg:pb-1.5">
              <span className="block truncate pr-1.5 text-right text-[10.5px] font-medium text-white/95 lg:pr-2 lg:text-[11.5px]">
                {item.guestName}
              </span>
            </span>
          )}
        </button>
      ))}
    </div>
  )
}

function PendingTile({ item }: { item: QueueItem }) {
  return (
    <div className="placeholder-warm-alt rounded-tile lg:rounded-tile-lg relative aspect-square overflow-hidden">
      {item.poster && (
        <img src={item.poster} alt="" className="h-full w-full object-cover blur-[1px]" />
      )}

      <div className="bg-shell/72 absolute inset-0 flex flex-col items-center justify-center gap-1.5">
        <Spinner className="text-accent size-5.5 lg:size-6.5" />
        <span className="text-ink-soft text-[11px] lg:text-xs">
          {item.status === 'preparing' ? 'klaarmaken' : 'uploaden'}
        </span>
      </div>
    </div>
  )
}
